import React, {useState} from 'react';
import {
  Box,
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  statusButtonStyles,
  statusMenuPaperStyles,
  statusMenuItemStyles,
  statusChipStyles,
  statusLoadingSpinnerStyles,
  statusArrowIconStyles,
} from './style';

export interface StatusOption {
  value: string;
  label: string;
  color: string;
}

interface StatusDropdownProps {
  value: string;
  options: StatusOption[];
  onChange: (status: string) => void;
  disabled?: boolean;
  loading?: boolean;
  disabledTooltip?: string;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  value,
  options,
  onChange,
  disabled = false,
  loading = false,
  disabledTooltip,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const current = options.find(opt => opt.value === value);

  const getDisplayLabel = () => {
    if (value === 'new_claim') {
      return 'New claim';
    }
    return current?.label || value;
  };

  const getDisplayColor = () => {
    if (value === 'new_claim') {
      return 'rgba(71, 84, 103, 1)';
    }
    return current?.color || '#000';
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (status: string) => {
    onChange(status);
    handleClose();
  };

  return (
    <Box>
      <Tooltip
        title={disabled && disabledTooltip ? disabledTooltip : ''}
        placement="top"
        arrow
      >
        <Box
          onClick={disabled ? undefined : handleOpen}
          sx={statusButtonStyles(getDisplayColor(), disabled, loading)}
          data-testid="status-dropdown"
        >
          {getDisplayLabel()}
          <KeyboardArrowDownIcon sx={statusArrowIconStyles} />
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        PaperProps={{sx: statusMenuPaperStyles}}
      >
        {options.map(opt => (
          <MenuItem
            key={opt.value}
            onClick={() => handleSelect(opt.value)}
            selected={value === opt.value}
            sx={statusMenuItemStyles}
          >
            <Chip
              label={opt.label}
              sx={{
                ...statusChipStyles,
                backgroundColor: opt.color,
              }}
            />
          </MenuItem>
        ))}
      </Menu>
      {loading && (
        <CircularProgress size={20} sx={statusLoadingSpinnerStyles} />
      )}
    </Box>
  );
};

export default StatusDropdown;
